/**
 * Item viewers for the agent assignment player.
 * ModuleViewer (slide-by-slide), PdfViewer (page tracking), VideoViewer
 * (resume position), YouTubeViewer + LinkViewer (manual completion).
 */
import { useEffect, useState, useCallback, useRef } from 'react';
import { ChevronLeft, ChevronRight, ExternalLink, CheckCircle2 } from 'lucide-react';
import { useApi } from '../../hooks/useApi';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { flattenSlides } from '../player/flattenSlides';

export function ModuleViewer({ item, onProgress }) {
  const { get } = useApi();
  const [slides, setSlides] = useState([]);
  const [idx, setIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const startIdx = useRef(Math.max(0, ...(item.progress?.viewed_units || [-1])));

  useEffect(() => {
    let cancelled = false;
    get(`/me/learning/modules/${item.ref_id}/content`)
      .then(m => {
        if (cancelled) return;
        const flat = flattenSlides(m).filter(s => s.type === 'slide');
        setSlides(flat);
        setIdx(Math.min(startIdx.current, Math.max(0, flat.length - 1)));
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [get, item.ref_id]);

  useEffect(() => {
    if (slides.length > 0) onProgress({ unit_index: idx, unit_total: slides.length });
  }, [idx, slides.length, onProgress]);

  if (loading) return <p className="text-sm text-muted-foreground p-6">Loading slides…</p>;
  if (slides.length === 0) return <p className="text-sm text-muted-foreground p-6">No slides in this module.</p>;

  const cur = slides[idx]?.slide || {};
  return (
    <div data-testid="module-viewer">
      <div className="bg-slate-950 rounded-lg overflow-hidden flex items-center justify-center min-h-[320px]">
        {cur.image_url ? (
          <img src={cur.image_url} alt={cur.title || 'Slide'} className="max-h-[60vh] w-full object-contain" />
        ) : (
          <div className="p-8 text-white max-w-2xl">
            <h3 className="font-display text-xl font-semibold mb-3">{cur.title}</h3>
            {cur.content?.text && <p className="text-slate-300 leading-relaxed">{cur.content.text}</p>}
            {cur.content?.bullet_points?.length > 0 && (
              <ul className="list-disc pl-5 mt-3 space-y-1 text-slate-300">
                {cur.content.bullet_points.map((bp, i) => <li key={i}>{bp}</li>)}
              </ul>
            )}
          </div>
        )}
      </div>
      <div className="flex items-center justify-between mt-3">
        <Button variant="outline" size="sm" disabled={idx === 0}
                onClick={() => setIdx(i => i - 1)} data-testid="slide-prev-btn">
          <ChevronLeft className="w-4 h-4 mr-1" /> Prev
        </Button>
        <span className="text-sm text-muted-foreground" data-testid="slide-counter">
          Slide {idx + 1} / {slides.length}
        </span>
        <Button size="sm" disabled={idx >= slides.length - 1}
                onClick={() => setIdx(i => i + 1)} data-testid="slide-next-btn">
          Next <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>
  );
}

export function PdfViewer({ item, onProgress }) {
  const total = item.meta?.unit_total || 0;
  const [page, setPage] = useState(Math.max(0, ...(item.progress?.viewed_units || [0])));

  useEffect(() => {
    if (total > 0) onProgress({ unit_index: page, unit_total: total });
  }, [page, total, onProgress]);

  if (!total) {
    return <OpenAndComplete item={item} onProgress={onProgress} label="Open document" />;
  }
  return (
    <div data-testid="pdf-viewer">
      <iframe title={item.title} src={`${item.url}#page=${page + 1}`}
              className="w-full h-[60vh] rounded-lg border" key={page} />
      <div className="flex items-center justify-between mt-3">
        <Button variant="outline" size="sm" disabled={page === 0}
                onClick={() => setPage(p => p - 1)} data-testid="pdf-prev-btn">
          <ChevronLeft className="w-4 h-4 mr-1" /> Prev page
        </Button>
        <span className="text-sm text-muted-foreground" data-testid="pdf-page-counter">
          Page {page + 1} / {total}
        </span>
        <Button size="sm" disabled={page >= total - 1}
                onClick={() => setPage(p => p + 1)} data-testid="pdf-next-btn">
          Next page <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>
  );
}

export function VideoViewer({ item, onProgress }) {
  const ref = useRef(null);
  const lastSaved = useRef(0);

  const save = useCallback(() => {
    const v = ref.current;
    if (!v || !v.duration) return;
    onProgress({ video_position: v.currentTime, video_duration: v.duration });
  }, [onProgress]);

  return (
    <div data-testid="video-viewer">
      <video
        ref={ref}
        src={item.url}
        controls
        className="w-full rounded-lg bg-black max-h-[60vh]"
        onLoadedMetadata={() => {
          const pos = item.progress?.video_position || 0;
          if (ref.current && pos > 1 && pos < (ref.current.duration || Infinity) - 3) {
            ref.current.currentTime = pos;
          }
        }}
        onTimeUpdate={() => {
          const v = ref.current;
          if (v && v.currentTime - lastSaved.current >= 10) {
            lastSaved.current = v.currentTime;
            save();
          }
        }}
        onPause={save}
        onEnded={() => {
          const v = ref.current;
          if (v) onProgress({ video_position: v.duration, video_duration: v.duration });
        }}
      />
      <p className="text-xs text-muted-foreground mt-2">
        Your position is saved automatically — resume anytime.
      </p>
    </div>
  );
}

function toYouTubeEmbed(url) {
  try {
    const u = new URL(url);
    if (u.hostname.includes('youtu.be')) return `https://www.youtube.com/embed/${u.pathname.slice(1)}`;
    if (u.searchParams.get('v')) return `https://www.youtube.com/embed/${u.searchParams.get('v')}`;
    if (u.pathname.startsWith('/embed/')) return url;
  } catch { /* fallthrough */ }
  return url;
}

export function YouTubeViewer({ item, onProgress }) {
  return (
    <div data-testid="youtube-viewer">
      <iframe title={item.title} src={toYouTubeEmbed(item.url)}
              className="w-full aspect-video rounded-lg border" allowFullScreen />
      <CompleteButton item={item} onProgress={onProgress} label="Mark as watched" />
    </div>
  );
}

export function LinkViewer({ item, onProgress }) {
  return <OpenAndComplete item={item} onProgress={onProgress} label="Open link" />;
}

function OpenAndComplete({ item, onProgress, label }) {
  return (
    <Card className="p-8 text-center" data-testid="link-viewer">
      <ExternalLink className="w-10 h-10 mx-auto text-muted-foreground/50 mb-3" />
      <p className="font-medium mb-4">{item.title}</p>
      <div className="flex justify-center gap-2">
        <Button asChild variant="outline" data-testid="open-link-btn">
          <a href={item.url} target="_blank" rel="noreferrer">{label}</a>
        </Button>
        <CompleteButton item={item} onProgress={onProgress} label="Mark complete" inline />
      </div>
    </Card>
  );
}

function CompleteButton({ item, onProgress, label, inline }) {
  if (item.progress?.completed) {
    return (
      <span className={`inline-flex items-center gap-1 text-sm text-emerald-700 ${inline ? '' : 'mt-3'}`}>
        <CheckCircle2 className="w-4 h-4" /> Completed
      </span>
    );
  }
  return (
    <Button className={inline ? '' : 'mt-3'} size="sm"
            onClick={() => onProgress({ mark_complete: true })}
            data-testid="mark-complete-btn">
      <CheckCircle2 className="w-4 h-4 mr-1" /> {label}
    </Button>
  );
}
